/**
 * Client-side photo downscaling for the driver audit flow. Phone cameras
 * emit 3–12MB HEIC/JPEG originals; audit-submit carries photos as base64
 * inside one JSON POST (Netlify's synchronous function payload cap is 6MB),
 * so each shot is resized to fit a bounding box and re-encoded as JPEG
 * before upload.
 */

const MAX_DIMENSION = 1600;
const JPEG_QUALITY = 0.78;

export interface CapturedPhoto {
  /** base64 payload WITHOUT the data: prefix (what audit-submit expects). */
  data_base64: string;
  content_type: 'image/jpeg';
  /** Object URL for the on-screen thumbnail. Revoke when discarded. */
  previewUrl: string;
}

export async function compressPhoto(file: File): Promise<CapturedPhoto> {
  const bitmap = await loadBitmap(file);
  const scale = Math.min(1, MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas unavailable');
  ctx.drawImage(bitmap, 0, 0, w, h);
  if ('close' in bitmap && typeof bitmap.close === 'function') bitmap.close();

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('photo encode failed'))),
      'image/jpeg',
      JPEG_QUALITY,
    );
  });

  const buf = await blob.arrayBuffer();
  let binary = '';
  const bytes = new Uint8Array(buf);
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }

  return {
    data_base64: btoa(binary),
    content_type: 'image/jpeg',
    previewUrl: URL.createObjectURL(blob),
  };
}

async function loadBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
  // createImageBitmap handles EXIF orientation on modern browsers (including
  // iOS 15+); fall back to an <img> decode for anything older or for HEIC
  // files the browser can decode natively but createImageBitmap rejects.
  try {
    return await createImageBitmap(file);
  } catch {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('could not read that photo — try a different one'));
      };
      img.src = url;
    });
  }
}
